import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { bringIntoView } from "../../ui/scroll";
import type { Menu, Product } from "../../api/types";
import { t } from "../../i18n";
import { eurosToCents, formatAmount } from "../../lib/money";
import { GripIcon } from "../../ui/icons";
import { rowStyle, useReorder } from "../../ui/useReorder";
import { priceKey, sizeShort, typedPrice } from "./menu-edit";
import type { QuickPrices } from "./useQuickPrices";

interface Props {
  menu: Menu;
  /** In the order they are shown. */
  products: Product[];
  /**
   * The rows of one category: they can be put in another order. What a search finds is of several
   * categories: nothing to hold there, and each row names its category.
   */
  sortable: boolean;
  /** The menu is being saved: no row can be taken. */
  busy: boolean;
  /** The product that is open next to the rows. */
  selected: string | null;
  soldOut: ReadonlySet<string>;
  /** The prices typed in the list, saved at once. */
  prices: QuickPrices;
  onOpen: (product: Product) => void;
  /** A row was let go at another place (0 is the first). */
  onMove: (productId: string, to: number) => void;
}

/**
 * The rows of Menu & prijzen › Producten: a product with its code and its price; a tap opens it. A row
 * of a category can be held and slid to another place (Furkan, 03/10/2026): by its grip at once, with
 * the mouse by the row itself, with a finger by resting on it; the arrows on the grip move it one place.
 *
 * A price is changed in the row itself (Furkan, 09/10/2026): a tap on the price opens it, Enter saves it
 * and opens the next price of the list, Shift+Enter the one before, Escape leaves it as it was. So a
 * whole category gets its new prices without opening one product.
 *
 * The row that is held lives here and not in the screen around it, so sliding draws these rows again and
 * not the product that is open next to them.
 */
export function ProductRows({ menu, products, sortable, busy, selected, soldOut, prices, onOpen, onMove }: Props) {
  const text = t.menuAdmin.order;
  const { taken, listRef, rowHandlers, gripHandlers } = useReorder(
    products.map((product) => product.id),
    { disabled: !sortable || busy, onMove },
  );
  /** The price that is open, by `priceKey`. */
  const [open, setOpen] = useState<string | null>(null);
  /** A price closed with the keys gives the keys back to its own button; a tap elsewhere keeps them there. */
  const refocus = useRef<string | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  /** Every price of the list in the order they stand: Enter goes to the next one. */
  const keys = products.flatMap((product) => product.variants.map((_, index) => priceKey(product.id, index)));
  const step = (from: string, by: 1 | -1) => {
    const next = keys[keys.indexOf(from) + by];
    setOpen(next ?? null);
    if (next === undefined) refocus.current = from;
  };
  useLayoutEffect(() => {
    const key = refocus.current;
    if (open !== null || key === null) return;
    refocus.current = null;
    for (const button of list.current?.querySelectorAll<HTMLElement>("[data-price]") ?? []) {
      if (button.dataset.price === key) button.focus();
    }
  }, [open]);

  return (
    <div
      className={`mprods${taken ? " moving" : ""}`}
      ref={(element) => {
        listRef(element);
        list.current = element;
      }}
    >
      {products.map((product, index) => (
        <div
          key={product.id}
          className={`mprod${product.id === selected ? " on" : ""}`}
          data-row={sortable ? product.id : undefined}
          data-taken={taken?.id === product.id ? true : undefined}
          style={rowStyle(taken, product.id, index)}
          {...(sortable ? rowHandlers : {})}
        >
          {sortable && (
            <button
              type="button"
              className="mgrip"
              aria-label={text.grip(product.name)}
              title={text.gripHint}
              data-grip={product.id}
              {...gripHandlers}
            >
              <GripIcon />
            </button>
          )}
          <div className="mprodmain">
            <button
              type="button"
              className="mprodsel"
              aria-pressed={product.id === selected}
              onClick={() => {
                onOpen(product);
              }}
            >
              <span>
                <b>{product.name}</b> <span className="code">{product.code}</span>
                {soldOut.has(product.id) && <span className="pill bad">{t.menuAdmin.soldOut}</span>}
                {!sortable && (
                  <>
                    <br />
                    <span className="note">
                      {menu.categories.find((item) => item.id === product.categoryId)?.name ?? ""}
                    </span>
                  </>
                )}
              </span>
            </button>
            <span className="mprices">
              {product.variants.map((variant, index) => {
                const key = priceKey(product.id, index);
                const price = prices.pending.get(key) ?? variant.price;
                const size = product.variants.length > 1 ? sizeShort(variant.label) : "";
                const label = t.menuAdmin.prices.edit(product.name, product.variants.length > 1 ? variant.label : "");
                return key === open ? (
                  <PriceField
                    key={key}
                    size={size}
                    label={label}
                    price={price}
                    onDone={(typed, to, byKeys) => {
                      if (typed !== null && typed !== price) prices.set(key, typed);
                      if (to !== 0) step(key, to);
                      else {
                        if (byKeys) refocus.current = key;
                        setOpen(null);
                      }
                    }}
                  />
                ) : (
                  <button
                    key={key}
                    type="button"
                    className={`mprice num${prices.pending.has(key) ? " saving" : ""}`}
                    aria-label={`${label}: ${formatAmount(eurosToCents(price))}`}
                    title={t.menuAdmin.prices.hint}
                    data-price={key}
                    onClick={() => {
                      setOpen(key);
                    }}
                  >
                    {size && <span className="msize">{size}</span>}
                    {formatAmount(eurosToCents(price))}
                  </button>
                );
              })}
            </span>
          </div>
        </div>
      ))}
      {products.length === 0 && <span className="note">{t.menuAdmin.noProducts}</span>}
    </div>
  );
}

interface FieldProps {
  /** "S", "G" or "33 cl" before the price; nothing for a product with one price. */
  size: string;
  label: string;
  price: number;
  /**
   * The field closes: with the typed price (null: left as it was), where to go, the next price (1),
   * the one before (-1) or nowhere (0), and whether a key closed it (Escape) or a tap elsewhere.
   */
  onDone: (price: number | null, to: 1 | -1 | 0, byKeys: boolean) => void;
}

/** One price, open in its row: the old price is selected, so typing replaces it. */
function PriceField({ size, label, price, onDone }: FieldProps) {
  const [text, setText] = useState(() => price.toFixed(2).replace(".", ","));
  const [wrong, setWrong] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  /** Enter, Tab or Escape closed it already: losing the focus afterwards is not a second close. */
  const closed = useRef(false);

  useLayoutEffect(() => {
    const input = field.current;
    if (!input) return;
    input.focus();
    input.select();
    // a long list follows the price that is open (a test's document has no scrolling)
    bringIntoView(input);
  }, []);

  const close = (to: 1 | -1 | 0, keep: boolean, byKeys: boolean) => {
    if (closed.current) return;
    if (!keep) {
      closed.current = true;
      onDone(null, to, byKeys);
      return;
    }
    const typed = typedPrice(text);
    if (Number.isNaN(typed)) {
      // a wrong price stays open to correct it; leaving it (a tap elsewhere) leaves the old price
      if (to === 0) {
        closed.current = true;
        onDone(null, 0, byKeys);
      } else setWrong(true);
      return;
    }
    closed.current = true;
    onDone(typed, to, byKeys);
  };

  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    const back = event.shiftKey ? -1 : 1;
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      close(back, true, true);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      close(event.key === "ArrowDown" ? 1 : -1, true, true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close(0, false, true);
    }
  };

  return (
    <label className={`mprice open${wrong ? " wrong" : ""}`}>
      {size && <span className="msize">{size}</span>}
      <input
        ref={field}
        className="num"
        inputMode="decimal"
        enterKeyHint="next"
        autoComplete="off"
        aria-label={label}
        aria-invalid={wrong || undefined}
        title={wrong ? t.menuAdmin.prices.wrong : t.menuAdmin.prices.keys}
        value={text}
        // typing in the price never takes the row to slide it
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onChange={(event) => {
          setText(event.target.value);
          setWrong(false);
        }}
        onKeyDown={keys}
        onBlur={() => {
          close(0, true, false);
        }}
      />
    </label>
  );
}
